import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchLiveRaces, fetchHorses, fetchTracks, fetchPredictionStats, logoutUser } from "../lib/api";
import { useAuth } from "../App";
import LiveRaceHero from "../components/LiveRaceHero";
import DailyTips from "../components/DailyTips";
import NewsSection from "../components/NewsSection";
import RadioPlayer from "../components/RadioPlayer";
import HorseCard from "../components/HorseCard";
import SearchBar from "../components/SearchBar";
import AlphabetFilter from "../components/AlphabetFilter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Horse, Funnel, Trophy, XCircle, ChartBar, Target, SignOut, WarningCircle, Crown } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user, setUser, allAccess } = useAuth() || {};
  const [liveRaces, setLiveRaces] = useState([]);
  const [horses, setHorses] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [letter, setLetter] = useState(null);
  const [trackFilter, setTrackFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalHorses, setTotalHorses] = useState(0);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([fetchLiveRaces(), fetchTracks()])
      .then(([lr, tr]) => { setLiveRaces(lr); setTracks(tr); })
      .catch(console.error);
    fetchPredictionStats().then(setStats).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchHorses({ letter: letter || undefined, track: trackFilter || undefined, limit: 50 })
      .then((data) => { setHorses(data.horses); setTotalHorses(data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [letter, trackFilter]);

  const handleLogout = async () => {
    await logoutUser();
    if (setUser) setUser(null);
    window.location.reload();
  };

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-zinc-800 px-4 md:px-8 py-3 bg-[#0a0a0a]">
        <div className="flex items-center justify-between gap-4 flex-wrap max-w-[1400px] mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-40.png" alt="Racing Form Guide" className="w-7 h-7" />
            <h1 className="text-lg sm:text-xl font-heading font-black tracking-tight text-white">
              Racing Form Guide
            </h1>
          </Link>

          <div className="hidden md:block flex-1 max-w-md mx-4">
            <SearchBar />
          </div>

          <nav className="flex items-center gap-3 text-xs font-mono uppercase tracking-wider">
            {allAccess && (
              <a
                href="https://rawcompanytech.com/members"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="all-access-badge"
                className="flex items-center gap-1.5 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-black px-3 py-1.5 text-[10px] font-bold tracking-widest hover:from-[#E5C040] hover:to-[#D4AF37] transition-all"
              >
                <Crown weight="fill" size={12} />
                RAW All-Access &#10003;
              </a>
            )}
            <Link to="/" data-testid="nav-home" className="text-[#D4AF37] font-bold hover:text-white transition-colors">Live</Link>
            <Link to="/tracks" data-testid="nav-tracks" className="text-zinc-400 hover:text-white transition-colors">Tracks</Link>
            <Link to="/account" data-testid="nav-account" className="text-zinc-400 hover:text-white transition-colors">Account</Link>
            {user && (
              <button data-testid="nav-logout" onClick={handleLogout} className="text-zinc-500 hover:text-[#E12B00] transition-colors flex items-center gap-1">
                <SignOut weight="bold" size={14} />
              </button>
            )}
          </nav>
        </div>
        <div className="md:hidden mt-3 max-w-[1400px] mx-auto">
          <SearchBar />
        </div>
      </header>

      <main className="p-4 md:p-8 max-w-[1400px] mx-auto">
        {/* Live Races Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 bg-[#E12B00] rounded-full live-pulse" />
            <p className="mono-label text-zinc-500">Today&apos;s Worldwide Races &mdash; Win chance calculated from overall career ratings + form</p>
          </div>
          <LiveRaceHero races={liveRaces} />
        </section>

        {/* Prediction Accuracy Stats */}
        <section data-testid="prediction-stats" className="mb-8 border border-zinc-800">
          <div className="bg-[#111] text-white px-4 py-3 flex items-center gap-2">
            <Target weight="bold" size={16} className="text-[#D4AF37]" />
            <span className="mono-label text-[#D4AF37]">Today&apos;s Prediction Accuracy</span>
          </div>
          {stats && stats.total_finished > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-800">
                <div className="p-4 text-center">
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-[#008A27]">{stats.correct}</p>
                  <p className="mono-label text-zinc-500 mt-1 flex items-center justify-center gap-1"><Trophy weight="fill" size={10} />Correct</p>
                </div>
                <div className="p-4 text-center">
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-[#E12B00]">{stats.wrong}</p>
                  <p className="mono-label text-zinc-500 mt-1 flex items-center justify-center gap-1"><XCircle weight="fill" size={10} />Wrong</p>
                </div>
                <div className="p-4 text-center">
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-white">{stats.accuracy_pct}%</p>
                  <p className="mono-label text-zinc-500 mt-1 flex items-center justify-center gap-1"><ChartBar weight="bold" size={10} />Win Rate</p>
                </div>
                <div className="p-4 text-center">
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-[#D4AF37]">{stats.top3_pct}%</p>
                  <p className="mono-label text-zinc-500 mt-1 flex items-center justify-center gap-1"><Target weight="bold" size={10} />In Top 3</p>
                </div>
              </div>
              <div className="px-4 py-2 bg-[#111] border-t border-zinc-800 text-[10px] font-mono text-zinc-500 text-center">
                Based on {stats.total_finished} finished races today &mdash; Our top pick won {stats.correct} times, finished in the top 3 in {stats.top3_correct} races
              </div>
            </>
          ) : (
            <div className="p-4 text-center">
              <p className="text-sm text-zinc-500">No finished races yet today &mdash; stats will update as races complete</p>
            </div>
          )}
        </section>

        {/* Daily Tips */}
        <DailyTips />

        {/* Live Radio Streams */}
        <RadioPlayer />

        {/* Racing News */}
        <NewsSection />

        {/* Disclaimer Banner */}
        <div data-testid="disclaimer-banner" className="mb-8 border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3 flex items-start gap-2">
          <WarningCircle weight="fill" size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-[#D4AF37]/80 font-mono">
            All win chance percentages are calculated by RAW Company Tech's AI algorithm and are NOT official bookmaker odds. For entertainment only. <Link to="/disclaimer" className="underline font-bold text-[#D4AF37]">Full disclaimer</Link>. Gamble responsibly. 18+
          </p>
        </div>

        {/* Horse Browser */}
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Horse weight="bold" size={16} className="text-zinc-400" />
              <p className="mono-label text-zinc-500">All Horses ({totalHorses})</p>
            </div>
            <div className="flex items-center gap-2">
              <Funnel weight="bold" size={14} className="text-zinc-400" />
              <Select value={trackFilter} onValueChange={(v) => setTrackFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="track-filter-select" className="w-[180px] h-9 rounded-none text-xs font-mono">
                  <SelectValue placeholder="Filter by track" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tracks</SelectItem>
                  {tracks.map((t) => (
                    <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mb-4">
            <AlphabetFilter selected={letter} onSelect={setLetter} />
          </div>

          {loading ? (
            <div className="py-12 text-center"><p className="mono-label text-zinc-400">Loading horses...</p></div>
          ) : horses.length === 0 ? (
            <div data-testid="no-horses" className="py-12 text-center border border-border">
              <p className="text-zinc-400 text-sm">No horses found matching your filters.</p>
            </div>
          ) : (
            <div data-testid="horse-list" className="border border-border divide-y divide-border">
              {horses.map((horse) => (
                <HorseCard key={horse.id} horse={horse} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
